import {
  Autocomplete,
  Checkbox,
  FormControl,
  Grid,
  TextField,
} from "@mui/material";
import { debounce, kebabCase } from "lodash";
import { FC, useCallback, useEffect, useState } from "react";
import { providerApi } from "../../../api/providerApi";
import useNotifications from "../../../hooks/useNotifications";
import { NotificationVariant } from "../../../components/dashboard/NotificationsProvider";
import { createReference } from "../../../utils/DataTransformationUtils";

interface OrganizationLookupInterface {
  disabled?: boolean;
  value: any;
  multiple?: any;
  label: string;
  onChange: (val: any) => void;
  isOptionEqualToValue?: (opt: any, value: any) => boolean;
}

const OrganizationLookup: FC<OrganizationLookupInterface> = ({
  value,
  disabled,
  multiple,
  label,
  onChange,
  isOptionEqualToValue,
}) => {
  const { setNotification } = useNotifications();

  const [options, setOptions] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [query, setQuery] = useState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const query: any = { q: "" };
    query.q = inputValue ?? "";
    setQuery(query);
  }, [inputValue]);

  useEffect(() => {
    debouncedSearch(inputValue, query);
  }, [inputValue, query]);

  const findOrganizations = async (inputValue, query) => {
    try {
      setLoading(true);
      if (inputValue !== undefined && query) {
        const orgs = await providerApi.findOrganizations({ q: inputValue });
        const opts = orgs.map((o) => {
          return { display: o.name, identifier: o.id };
        });
        setOptions(opts);
      }
    } catch (e) {
      setNotification({
        message: "Unable to search for organizations. An error occured",
        variant: NotificationVariant.error,
      });
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(debounce(findOrganizations, 300), []);

  return (
    <Grid item xs={12} style={{ paddingTop: 10 }}>
      <FormControl variant="outlined" fullWidth>
        <Autocomplete
          loading={loading}
          fullWidth
          multiple
          autoHighlight
          disabled={disabled}
          key={`organization-lookup-${kebabCase(label)}`}
          renderOption={(props, option: any, { selected }) => (
            <li {...props}>
              <Checkbox
                key={`org-${option.identifier}`}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.display}
            </li>
          )}
          onChange={(e, selected) => {
            if (selected && selected.length > 0) {
              if (multiple) {
                const refs = selected.map((s) => {
                  return createReference(
                    "Organization",
                    s.identifier,
                    s.display,
                  );
                });
                onChange(refs);
              } else {
                onChange([
                  createReference(
                    "Organization",
                    selected[0].identifier,
                    selected[0].display,
                  ),
                ]);
              }
            } else {
              onChange([]);
            }
          }}
          inputValue={inputValue}
          getOptionLabel={(option) => option.display}
          onInputChange={(event, newInput) => {
            setInputValue(newInput);
          }}
          isOptionEqualToValue={(option, value) =>
            isOptionEqualToValue
              ? isOptionEqualToValue(option, value)
              : option.identifier === value.identifier
          }
          options={options}
          value={value ?? []}
          renderInput={(params) => (
            <TextField {...params} label={label} maxRows={3} />
          )}
        />
      </FormControl>
    </Grid>
  );
};

export default OrganizationLookup;
